import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useCreateOffer } from '@/hooks/useWorkOrderOffers';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { useNotify } from '@/stores/notificationStore';
import { describeApiError } from '@/lib/describeApiError';
import { formatCentsForDisplay } from '@/utils/money';

interface CreateOfferDialogProps {
  open: boolean;
  workOrderId: string;
  items: WorkOrderDetail['items'];
  onClose: () => void;
}

export function CreateOfferDialog({
  open,
  workOrderId,
  items,
  onClose,
}: CreateOfferDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const createOffer = useCreateOffer();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [customerDescription, setCustomerDescription] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setTitle('');
    setCustomerDescription('');
    setSubmitError(null);
  }, [open]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canSubmit = selected.size > 0 && !createOffer.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    const data: { itemIds: string[]; title?: string; customerDescription?: string } = {
      itemIds: [...selected],
    };
    const trimmedTitle = title.trim();
    if (trimmedTitle) data.title = trimmedTitle;
    const trimmedDescription = customerDescription.trim();
    if (trimmedDescription) data.customerDescription = trimmedDescription;
    try {
      await createOffer.mutateAsync({ workOrderId, data });
      notify.success(t('offers.createdTitle'), t('offers.createdDesc'));
      onClose();
    } catch (error) {
      const messageText = describeApiError(error, t, {
        codePrefix: 'offers.errorCode',
        fallback: t('offers.createFailed'),
      });
      setSubmitError(messageText);
      notify.error(t('offers.createFailed'), messageText);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={createOffer.isPending ? undefined : onClose}
      mobileTitle={t('offers.createTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('offers.createTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {submitError && <Alert severity="error">{submitError}</Alert>}
        <Typography variant="subtitle2">{t('offers.selectItems')}</Typography>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('offers.noEligibleItems')}
          </Typography>
        ) : (
          <FormGroup>
            {items.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={selected.has(item.id)}
                    onChange={() => {
                      toggle(item.id);
                    }}
                  />
                }
                label={`${item.description} · ${formatCentsForDisplay(item.partsCostCents + item.laborCostCents)}`}
              />
            ))}
          </FormGroup>
        )}
        <TextField
          label={t('offers.offerTitleField')}
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />
        <TextField
          label={t('offers.customerDescription')}
          value={customerDescription}
          onChange={(event) => {
            setCustomerDescription(event.target.value);
          }}
          multiline
          minRows={2}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { maxLength: 4000 } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createOffer.isPending}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={!canSubmit}
          startIcon={createOffer.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {t('offers.createSubmit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
